import { DocumentationPageType, SubPageTypes } from './types';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectActiveDocumentationPageTab,
  setActivePageTab,
  selectDocumentationPageType,
  setDocumentationPageType,
  clearActivePageTab,
} from '../../reducers/documentation/documentationPageReducer';
import {
  selectSingleObject,
  selectSingleObjectActiveTab,
} from '../../reducers/documentation/salesforceDocumentationReducer';
import { DocumentationTabTypes } from '../../types/enums/DocumentationTabTypes';
import { useObjectListInteractions } from './object-list/useObjectListInteractions';
import { useObjectListUniversalSearch } from './object-list/useObjectListUniversalSearch';
import { getMetadataLabel } from './tabs/getMetadataLabel';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import { DocumentationLightbox } from './universal-search/lightbox/DocumentationLightbox';
import { AccountType } from '@server/account-type';
import { selectUserInfoData } from '../../reducers/userInfoReducer';
import { useExpandedMode } from '../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { Breadcrumb } from './selected-object/Breadcrumbs';
import { DocumentationPageHeaderLayout } from './building-blocks/DocumentationPageHeaderLayout';

const pageTypeToName: { [key in SubPageTypes]: string } = {
  [SubPageTypes.objects]: 'Objects',
  [SubPageTypes.tags]: 'Tags',
  [SubPageTypes.users]: 'Sweep Users',
};

interface DocumentationPageHeaderProps {
  onCloseDialog?: () => void;
  withExpandedModeButton: boolean;
  renderBreadcrumbs?: boolean;
  crmOrgId: string;
}

export const DocumentationPageHeader = ({
  onCloseDialog,
  withExpandedModeButton,
  renderBreadcrumbs,
  crmOrgId,
}: DocumentationPageHeaderProps) => {
  const userInfo = useSelector(selectUserInfoData);
  const isFreeUser = userInfo?.account?.type === AccountType.Free; //by product definition should block all (both trial and non trial users) free users
  const [isLightboxOpened, setIsLightboxOpened] = useState(false);
  const { universalSearchRedesign } = useFeatureToggle();

  const { isExpandedMode } = useExpandedMode();
  const dispatch = useDispatch();
  const documentationPageType = useSelector(selectDocumentationPageType);
  const activePageTab = useSelector(selectActiveDocumentationPageTab);
  const objectTypeName = useSelector(selectSingleObject);
  const activeObjectTab = useSelector(selectSingleObjectActiveTab);
  const isObjectCardActiveTab =
    (activeObjectTab && activeObjectTab === DocumentationTabTypes.CARDS_LIST) || isExpandedMode;

  const { backToObjectList, backToElementsList } = useObjectListInteractions();
  const { handleCloseUniversalSearch } = useObjectListUniversalSearch({
    crmOrgId: '', // the crmOrgId is not used in this context
  });

  const resetTabState = useCallback(() => {
    handleCloseUniversalSearch();
    dispatch(clearActivePageTab());
    dispatch(
      setDocumentationPageType({ documentationPageType: DocumentationPageType.metadataTypes }),
    );
  }, [dispatch, handleCloseUniversalSearch]);

  const breadcrumbList = useMemo(() => {
    const isDocumentationPageTagsAndUsers =
      documentationPageType === DocumentationPageType.tagsAndUsers;
    const isDocumentationPageUniversalSearch =
      documentationPageType === DocumentationPageType.universalSearch;
    const isDocumentationPageMetadataTypes =
      documentationPageType === DocumentationPageType.metadataTypes;

    const baseList: Breadcrumb[] = [
      {
        value: 'Documentation',
        label: 'Documentation',
        onClick: resetTabState,
      },
    ];

    if (isDocumentationPageTagsAndUsers) {
      baseList.push({
        value: 'Custom annotations',
        label: 'Custom annotations',
        onClick: () => {
          dispatch(setActivePageTab({ tabId: '' }));
        },
      });
    }

    if (isDocumentationPageUniversalSearch) {
      baseList.push({
        value: 'Search',
        label: 'Search',
        onClick: () => {},
      });
    }

    if (activePageTab && !isDocumentationPageUniversalSearch) {
      baseList.push({
        value: activePageTab as string,
        label: activePageTab
          ? ((pageTypeToName[activePageTab as SubPageTypes] as string) ??
            getMetadataLabel(activePageTab))
          : '',
        onClick: backToObjectList,
      });
    }

    if (isDocumentationPageMetadataTypes) {
      if (objectTypeName && !baseList.find((b) => b.value === objectTypeName.objectType)) {
        baseList.push({
          value: objectTypeName.objectType,
          label: objectTypeName.label,
          onClick: backToElementsList,
        });
      }

      if (objectTypeName && activeObjectTab && !isObjectCardActiveTab) {
        baseList.push({ label: activeObjectTab, value: activeObjectTab, onClick: () => {} });
      }
    }

    return baseList;
  }, [
    resetTabState,
    activePageTab,
    backToObjectList,
    objectTypeName,
    activeObjectTab,
    isObjectCardActiveTab,
    documentationPageType,
    backToElementsList,
    dispatch,
  ]);

  const isUniversalSearch = documentationPageType === DocumentationPageType.universalSearch;
  const showBreadcrumbs =
    !!renderBreadcrumbs &&
    (!!activePageTab || documentationPageType !== DocumentationPageType.metadataTypes);

  const handleSearchClick = useCallback(() => {
    if (universalSearchRedesign) {
      setIsLightboxOpened(true);
    } else {
      dispatch(
        setDocumentationPageType({
          documentationPageType: DocumentationPageType.universalSearch,
        }),
      );
    }
  }, [universalSearchRedesign, dispatch]);

  const lightbox =
    isLightboxOpened && universalSearchRedesign ? (
      <DocumentationLightbox onClose={() => setIsLightboxOpened(false)} crmOrgId={crmOrgId} />
    ) : undefined;

  return (
    <DocumentationPageHeaderLayout
      onCloseDialog={onCloseDialog}
      withExpandedModeButton={withExpandedModeButton}
      breadcrumbs={breadcrumbList}
      showBreadcrumbs={showBreadcrumbs}
      showSearch={!isUniversalSearch && !isFreeUser}
      onSearchClick={handleSearchClick}
      searchContent={lightbox}
    />
  );
};
